import _ from 'lodash';

import { LoanPipelineSpecificColumnId } from 'constants/loanPipelines';
import { TaskStatus } from 'constants/tasks';

import { MULTIPART_CONFIG, TASK_INCLUDE_CONFIG, TASKS_URL } from './taskApi.constants';
import { toTaskType } from './taskTypeApi';

const LOAN_PIPELINES_URL = '/api/loanPipelines';

const PIPELINE_TASK_ENTRY_TYPE = 'Task';

const PIPELINE_VALUE_MAX_LENGTH = 300;

const PIPELINE_VALUE_TRUNCATED_LENGTH = 290;

const GENERAL_TASK_ASSIGNMENT_TYPE = 'General';

export const toTask = ({
  id,
  lifeTimeInfo,
  documentRequest,
  paymentData,
  taskType,
  submittedDocuments,
  ...task
}) => ({
  ...task,
  taskId: id,
  createdDate: lifeTimeInfo?.createdDate ?? null,
  lastModified: lifeTimeInfo?.modifiedDate ?? null,
  documentRequestDays: documentRequest?.documentRequestDays ?? null,
  documentRequestedDate: documentRequest?.documentRequestedDate ?? null,
  documentRequestDeadline: documentRequest?.documentRequestDeadline ?? null,
  paymentAmount: paymentData?.amount ?? 0,
  paymentRequestId: paymentData?.requestId ?? null,
  taskType: taskType ? toTaskType(taskType, null) : null,
  submittedDocuments: submittedDocuments
    ? submittedDocuments.filter(({ isDeleted }) => !isDeleted)
    : null,
});

const toAssignTasksRequest = ({
  applicationId,
  taskTypeId,
  name,
  description,
  borrowerId = null,
  taskEntityId = null,
  formBundleId = null,
  availableForAssignees = false,
  metadata = null,
  isSignatureRequired = null,
  assignedUserId = null,
  notifyBorrower = true,
}) => ({
  applicationId,
  requests: [
    {
      type: GENERAL_TASK_ASSIGNMENT_TYPE,
      taskTypeId,
      name,
      description,
      borrowerId,
      taskEntityId,
      formId: formBundleId,
      availableForAssignees,
      metadata,
      isSignatureRequired,
      assignedUserId,
      notifyBorrower,
    },
  ],
  metadata: {},
});

export const toPatchFormData = (patch, files = []) => {
  const formData = new FormData();

  formData.append('request', JSON.stringify(patch));
  files.forEach(file => formData.append('uploadedFiles', file, file.name));

  return formData;
};

const toUpdatePatch = ({
  status,
  name,
  description,
  userNote,
  formBundleId = null,
  isSignatureRequired = null,
  submittedText,
  isDocumentRequested,
}) => ({
  status,
  name: name || null,
  description: description || null,
  userNote: userNote || null,
  formBundleId,
  isSignatureRequired,
  submittedText: submittedText || null,
  isDocumentRequested: !!isDocumentRequested,
  isDedicatedRouteUpdate: false,
});

const toPipelineValue = value =>
  value?.length >= PIPELINE_VALUE_MAX_LENGTH
    ? `${value.slice(0, PIPELINE_VALUE_TRUNCATED_LENGTH)}...`
    : (value ?? null);

const toPipelineViewItem = ({
  taskId,
  applicationId,
  name,
  status,
  userNote,
  submittedText,
  lastModified,
}) => ({
  entryId: { type: PIPELINE_TASK_ENTRY_TYPE, taskId, applicationId },
  columnValues: [
    { columnId: LoanPipelineSpecificColumnId.TASK_NAME, value: toPipelineValue(name) },
    { columnId: LoanPipelineSpecificColumnId.TASK_STATUS, value: toPipelineValue(status) },
    { columnId: LoanPipelineSpecificColumnId.TASK_USER_NOTE, value: toPipelineValue(userNote) },
    {
      columnId: LoanPipelineSpecificColumnId.TASK_SUBMITTED_TEXT,
      value: toPipelineValue(submittedText),
    },
    {
      columnId: LoanPipelineSpecificColumnId.TASK_LAST_MODIFIED,
      value: toPipelineValue(lastModified),
    },
  ],
});

const byEventDate = ({ eventDate }) => eventDate;

export class TaskApi {
  #httpLoans;

  constructor(httpLoans) {
    this.#httpLoans = httpLoans;
  }

  async #updateTask({ taskId, patch, files }) {
    const response = await this.#httpLoans.put(
      `${TASKS_URL}/${taskId}`,
      toPatchFormData(patch, files),
      MULTIPART_CONFIG,
    );

    return { ...response, data: toTask(response.data) };
  }

  async getTaskById({ taskId }) {
    const response = await this.#httpLoans.get(`${TASKS_URL}/${taskId}`, TASK_INCLUDE_CONFIG);

    return { ...response, data: toTask(response.data) };
  }

  async createTask(body) {
    const response = await this.#httpLoans.post(TASKS_URL, toAssignTasksRequest(body));

    return { ...response, data: toTask(response.data[0]) };
  }

  async approveTask({ taskId }) {
    return this.#updateTask({
      taskId,
      patch: { status: TaskStatus.APPROVED, isDedicatedRouteUpdate: true },
    });
  }

  async approveScheduleClosingTask({ taskId, selectedSlotIndex }) {
    return this.#updateTask({
      taskId,
      patch: {
        status: TaskStatus.APPROVED,
        scheduleClosingApproval: { selectedSlotIndex },
        isDedicatedRouteUpdate: true,
      },
    });
  }

  async rejectTask({ taskId, userNote }) {
    return this.#updateTask({
      taskId,
      patch: {
        status: TaskStatus.REJECTED,
        userNote: _.trim(userNote) ? userNote : null,
        isDedicatedRouteUpdate: true,
      },
    });
  }

  async getTaskHistory({ taskId }) {
    const response = await this.#httpLoans.get(`${TASKS_URL}/${taskId}/history`);

    return { ...response, data: _.sortBy(response.data, byEventDate) };
  }

  async postTaskMessage({ taskId, message, audience }) {
    return this.#httpLoans.post(`${TASKS_URL}/${taskId}/messages`, { message, audience });
  }

  async getLoanTasks({ applicationId, signal }) {
    const response = await this.#httpLoans.get(TASKS_URL, {
      ...TASK_INCLUDE_CONFIG,
      params: { ...TASK_INCLUDE_CONFIG.params, applicationId },
      signal,
    });

    return { ...response, data: response.data.map(toTask) };
  }

  async getTasksByIds({ taskIds, signal }) {
    const response = await this.#httpLoans.get(TASKS_URL, {
      ...TASK_INCLUDE_CONFIG,
      params: { ...TASK_INCLUDE_CONFIG.params, taskIds },
      signal,
    });

    return { ...response, data: response.data.map(toTask) };
  }

  async updateTask({ taskId, request, files }) {
    return this.#updateTask({ taskId, patch: toUpdatePatch(request), files });
  }

  async updatePipelineTask({ taskId, pipelineId, request, files }) {
    const { data: task } = await this.updateTask({ taskId, request, files });

    await this.#httpLoans.post(`${LOAN_PIPELINES_URL}/viewData`, [toPipelineViewItem(task)]);

    const response = await this.#httpLoans.post(
      `${LOAN_PIPELINES_URL}/${pipelineId}/dynamicViewData`,
      { entryIds: [task.taskId], pageNumber: 1, pageSize: 1 },
    );

    return { ...response, data: { task, pipelineEntryData: response.data } };
  }

  async getLoanSummaryTask({ applicationId }) {
    return this.#httpLoans.get(`${TASKS_URL}/summary`, { params: { applicationId } });
  }
}
